import { Send, Square, Plus } from 'lucide-react';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import beeIcon from '../../assets/icons/assistant-bee.png';
import owlIcon from '../../assets/icons/assistant-owl.png';
import squirrelIcon from '../../assets/icons/assistant-squirrel.png';
import { FILL_GRADIENT, LINE_GRADIENT } from '../../constants/gradients';
import LexicalPromptInput, { LexicalPromptInputHandle } from './prompt-input/LexicalPromptInput';
import { ELEMENT_SELECTED_COMMAND, EDIT_MODE_EXITED_COMMAND } from './prompt-input/PromptCommands';
import { promptPreparationService } from '../../services/PromptPreparationService';
import PromptItemSelector from './prompt-input/PromptItemSelector';
import { PromptItemType } from './prompt-input/PromptItemNode';

export interface AIPromptInputHandle {
  dispatchElementSelected: (editId: string, label: string) => void;
  dispatchEditModeExited: () => void;
  insertAsset: (assetId: string, assetName: string) => void;
  focus: () => void;
}

interface AIPromptInputProps {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onApply: (cleanedPrompt: string, promptItems: any[]) => void;
  onAbort?: () => void;
  isProcessing: boolean;
  isStreaming?: boolean;
  streamingProgress?: number;
  activeTab: 'play' | 'code' | 'edit';
  onTabChange: (tab: 'play' | 'code' | 'edit') => void;
  selectedModel?: 'norm' | 'exp' | 'eco';
  processingTime: number;
  currentProcessingMessage: string;
  processingMessageKey: number;
  isLimitReached?: boolean;
  // For prompt preparation
  iframeRef?: React.RefObject<HTMLIFrameElement>;
  projectId: string;
  onOpenAssets?: () => void;
}

const PLACEHOLDER_EXAMPLES = [
  'Create a trivia game with score counter...',
  'Make a word guessing game...',
  'Build a tap timing game...',
  'Create a poll with results display...',
  'Make a color matching game...',
  'Build a reaction speed game...',
  'Create a quiz with multiple choice answers...',
  'Make a drawing guessing game...',
  'Build a coin flip simulator...',
  'Create a dice rolling game...'
];

const MODEL_INFO = {
  norm: { name: 'Bee', icon: beeIcon },
  exp: { name: 'Owl', icon: owlIcon },
  eco: { name: 'Squirrel', icon: squirrelIcon }
};

const AIPromptInput = forwardRef<AIPromptInputHandle, AIPromptInputProps>(({
  prompt,
  onPromptChange,
  onApply,
  onAbort,
  isProcessing,
  isStreaming = false,
  streamingProgress = 0,
  isLimitReached = false,
  activeTab,
  onTabChange,
  selectedModel = 'norm',
  processingTime,
  currentProcessingMessage,
  processingMessageKey,
  iframeRef,
  projectId,
  onOpenAssets,
}, ref) => {
  const [placeholder, setPlaceholder] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const editorRef = useRef<LexicalPromptInputHandle>(null);
  const prevActiveTabRef = useRef<'play' | 'code' | 'edit'>(activeTab);

  const currentModelInfo = MODEL_INFO[selectedModel];

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    dispatchElementSelected: (editId: string, label: string) => {
      if (editorRef.current) {
        const editor = editorRef.current.getEditor();
        if (editor) {
          editor.dispatchCommand(ELEMENT_SELECTED_COMMAND, { editId, label });
        }
      }
    },
    dispatchEditModeExited: () => {
      if (editorRef.current) {
        const editor = editorRef.current.getEditor();
        if (editor) {
          editor.dispatchCommand(EDIT_MODE_EXITED_COMMAND, undefined);
        }
      }
    },
    insertAsset: (assetId: string, assetName: string) => {
      if (editorRef.current) {
        editorRef.current.insertItem({
          type: 'asset',
          label: assetName,
          id: assetId,
          metadata: { assetId },
        });
      }
    },
    focus: () => {
      if (editorRef.current) {
        editorRef.current.focus();
      }
    },
  }), []);

  useEffect(() => {
    // Select random placeholder on mount
    const randomIndex = Math.floor(Math.random() * PLACEHOLDER_EXAMPLES.length);
    setPlaceholder(PLACEHOLDER_EXAMPLES[randomIndex]);
  }, []);

  // Watch for tab changes and dispatch EDIT_MODE_EXITED when leaving 'edit' mode
  useEffect(() => {
    if (prevActiveTabRef.current === 'edit' && activeTab !== 'edit') {
      if (editorRef.current) {
        const editor = (editorRef.current as any).editor;
        if (editor) {
          editor.dispatchCommand(EDIT_MODE_EXITED_COMMAND, undefined);
        }
      }
    }
    prevActiveTabRef.current = activeTab;
  }, [activeTab]);

  const handleAddItem = () => {
    if (onOpenAssets) {
      onOpenAssets();
    } else {
      setShowSelector(true);
    }
  };

  const handleSelectItem = (type: PromptItemType, label: string, assetId?: string) => {
    if (!editorRef.current) {
      console.error('Editor ref is null!');
      return;
    }
    editorRef.current.insertItem({
      type,
      label,
      id: assetId || `${type}-${Date.now()}`,
      metadata: assetId ? { assetId } : {},
    });
    setShowSelector(false);
    // Focus the editor after inserting
    editorRef.current.focus();
  };

  const handleCancelSelector = () => {
    setShowSelector(false);
  };

  const handleSubmit = () => {
    if (prompt.trim()) {
      // Prepare prompt with context using the service
      const prepared = promptPreparationService.prepare(
        editorRef.current,
        iframeRef,
        prompt
      );
      
      // Pass cleaned prompt and items separately to parent
      onApply(prepared.text, prepared.promptItems);
    }
  };

  const handleFocusChange = (focused: boolean) => {
    setIsFocused(focused);
  };

  return (
    <div className="px-4 py-3 pb-4 relative flex items-end bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-dark-border">
      <div className="w-full relative ai-prompt-input-container flex items-start gap-2">
        {/* Plus button to add prompt items */}
        <div className="relative">
          <button
            onClick={handleAddItem}
            disabled={isProcessing || isStreaming || isLimitReached}
            className="mt-[6px] flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Add asset"
          >
            <Plus className="w-5 h-5" />
          </button>
          {showSelector && (
            <PromptItemSelector
              onSelect={handleSelectItem}
              onCancel={handleCancelSelector}
              projectId={projectId}
            />
          )}
        </div>

        {/* Lexical Editor Container */}
        <div className="flex-1 relative mb-4">
          {/* Gradient border wrapper */}
          <div 
            className="rounded-[10px]"
            style={{
              padding: '2px',
              background: isFocused && !isLimitReached ? LINE_GRADIENT : 'transparent'
            }}
          >
            <div 
              className="relative bg-white dark:bg-gray-800 rounded-lg"
              style={{
                border: isFocused && !isLimitReached ? '0' : isLimitReached ? '2px solid #b91c1c' : '1px solid #d1d5db',
              }}
            >
              <LexicalPromptInput
                ref={editorRef}
                value={prompt}
                onChange={onPromptChange}
                onSubmit={handleSubmit}
                placeholder={isLimitReached ? 'Daily usage limit reached.' : placeholder}
                disabled={isProcessing || isStreaming || isLimitReached}
                onFocusChange={handleFocusChange}
              />
              
              <button
                onClick={(isProcessing || isStreaming) ? (onAbort || (() => {})) : handleSubmit}
                disabled={!(isProcessing || isStreaming) && (!prompt.trim() || isLimitReached)}
                className={`absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                  !(isProcessing || isStreaming) && (!prompt.trim() || isLimitReached)
                    ? 'bg-gray-300 dark:bg-gray-700 opacity-50' 
                    : 'hover:scale-105 active:scale-95 hover:shadow-lg'
                }`}
                style={!(isProcessing || isStreaming) && (!prompt.trim() || isLimitReached) ? {} : { background: FILL_GRADIENT }}
                title={(isProcessing || isStreaming) ? 'Stop generation' : 'Send prompt'}
              >
                {(isProcessing || isStreaming) ? (
                  <Square className="w-4 h-4 fill-current text-white" />
                ) : (
                  <Send className="w-4 h-4 fill-current text-white" />
                )}
              </button>
              
              {/* Streaming Progress Indicator */}
              {isStreaming && streamingProgress > 0 && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full transition-all duration-300"
                    style={{ 
                      width: `${streamingProgress}%`,
                      background: LINE_GRADIENT 
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        
      </div>
    </div>
  );
});

AIPromptInput.displayName = 'AIPromptInput';

export default AIPromptInput;
